import { Component, computed, effect, ElementRef, inject, OnDestroy, signal, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TreeNode, TreeView } from '../tree-view/tree-view';
import { AppService } from 'src/app/services/app.service';
import { OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import { filter, map, Subscription } from 'rxjs';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDialog } from '@angular/material/dialog';
import { TemplatePortal } from '@angular/cdk/portal';
import { Dialog } from 'src/app/components/dialog/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { MatIconButton } from "@angular/material/button";
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MatBadge } from "@angular/material/badge";
import { NotificationService } from 'src/app/services/notification.service';
import { AuthService, PermissionCombinator } from 'src/app/auth';
import { toSignal } from '@angular/core/rxjs-interop';

/** One icon in the app-switcher row below the logo. A `children` entry switches the menu into that
 *  section (see `activeSection`); a bare `path` entry (no children, nothing to switch into) just
 *  navigates directly, like any other single-destination nav item. */
interface SwitcherItem {
  key: string;
  label: string;
  icon: string;
  permission?: PermissionCombinator;
  show?: boolean;
  children?: TreeNode[];
  path?: string[];
  /** a small tag on the tile, e.g. "Test" for an area still in its test phase */
  badge?: string;
}

/** Sektions-Key der Einstellungen. Liegt im gleichen Namensraum wie die `key`s der switcherItems
 *  (activeSection/lastAppSection halten genau einen davon), taucht dort aber nie als Icon-Button auf. */
const SETTINGS_SECTION_KEY = 'settings';

@Component({
  selector: 'menu-side-bar',
  imports: [CommonModule, TreeView, MatDialogModule, MatIconModule, MatRippleModule, MatIconButton, MatBadge, RouterLink, RouterLinkActive],
  templateUrl: './menu-side-bar.html',
  styleUrl: './menu-side-bar.sass'
})
export class MenuSideBar implements OnDestroy {
  // signal to track selected tree node across nested views
  selectedNode = signal<string | null>(null);
  notificationService = inject(NotificationService)
  authService = inject(AuthService);
  oauth = inject(OAuthService);
  notificationStatus = computed<number>(() => this.notificationService.notificationCount());
  app = inject(AppService)
  userInfo = signal('');
  /** Initiale für das Avatar-Badge im Footer. */
  userInitial = computed<string>(() => this.userInfo().trim().charAt(0).toUpperCase() || '?');
  sub: Subscription = new Subscription();

  @ViewChild('logoutTemplate', { static: true }) logoutTemplate!: TemplateRef<unknown>;
  readonly dialog = inject(MatDialog);
  private vcr = inject(ViewContainerRef);
  private readonly router = inject(Router);

  /** Ob die App-Switcher-Sektion (hinter dem "more"-Button) gerade aufgeklappt ist. Die Sektion
   *  bleibt dabei Teil der Menueleiste und schiebt die Navigation nach unten (siehe .switcher-section
   *  in menu-side-bar.sass) - nichts wird ueberlappt. */
  switcherOpen = signal(false);

  @ViewChild('switcherTrigger', { read: ElementRef }) private switcherTrigger?: ElementRef<HTMLElement>;
  @ViewChild('switcherSection', { read: ElementRef }) private switcherSection?: ElementRef<HTMLElement>;

  /** Eigener Button neben dem "more"-Button (frueher Teil der Dashboard/Einstellungen-Baumliste). */
  dashboardVisible = computed<boolean>(() => {
    this.authService.authStateChanged();
    return this.authService.hasPermission({ or: ['reports.read'] });
  });

  /** Einstellungen: immer sichtbar, unabhängig von den Switcher-Sektionen. Der Einstieg sitzt als
   *  eigener Eintrag im Footer (siehe settingsVisible), die Kinder (settingsNodes) erscheinen aber
   *  genau wie bei jeder Switcher-Sektion oben in .nav-scroll - Einstellungen ist damit eine
   *  vollwertige Sektion (siehe settingsSection/activeSectionNodes), nur ohne Icon-Button im
   *  App-Switcher. */
  settingsVisible = computed<boolean>(() => {
    this.authService.authStateChanged();
    return this.authService.hasPermission({ or: ['studios.read', 'studios.write', 'users.read', 'users.write', 'team.manage'] });
  });

  settingsNodes = computed<TreeNode[]>(() => {
    this.authService.authStateChanged();
    return [
      {
        label: 'Studio',
        permission: { or: ['studios.read', 'studios.write'] },
        path: ["settings", "studio"]
      },
      {
        label: 'Standorte',
        permission: { or: ['studios.read', 'studios.write'] },
        path: ["settings", "locations"]
      },
      {
        label: 'E-Mail Konten',
        permission: { or: ['studios.read', 'studios.write'] },
        path: ["settings", "email-accounts"]
      },
      {
        label: 'Kategorie-Gruppen',
        expanded: false,
        permission: { or: ['studios.read', 'studios.write'] },
        path: ["category-groups"]
      },
      {
        label: 'Zusatzfelder',
        permission: { or: ['studios.read', 'studios.write'] },
        path: ["settings", "user-fields"]
      },
      {
        label: 'Antwortvorlagen',
        permission: { or: ['studios.read', 'studios.write'] },
        path: ["settings", "reply-templates"]
      },
      {
        label: 'Bögen & Anamnese',
        expanded: false,
        permission: { or: ['studios.read', 'studios.write'] },
        path: ["forms"]
      },
      {
        label: 'Team & Rollen',
        expanded: false,
        permission: { or: ['team.manage', 'users.write'] },
        path: ["team"]
      },
      {
        // today only Memberbox, which feeds the finance area
        label: 'Integrationen',
        permission: { or: ['finance.write'] },
        show: this.authService.hasFeature('finance'),
        path: ["settings", "integrationen"]
      }
    ];
  });

  /** Einstellungen als Sektion - gleiche Form wie ein Switcher-Item mit Kindern, damit
   *  activeSectionNodes und selectSection sie ohne Sonderfall behandeln koennen. Bewusst NICHT Teil
   *  von switcherItems: der Einstieg bleibt der Footer-Eintrag, nicht ein Icon im App-Switcher. */
  readonly settingsSection = computed<SwitcherItem>(() => ({
    key: SETTINGS_SECTION_KEY,
    label: 'Einstellungen',
    icon: 'settings',
    children: this.settingsNodes(),
  }));

  /** Ob die Einstellungen gerade die aktive Sektion sind - markiert den Footer-Eintrag, analog zur
   *  Hervorhebung des aktiven Switcher-Buttons. */
  settingsActive = computed<boolean>(() => this.activeSection() === SETTINGS_SECTION_KEY);

  /** Die frueheren Haupt-Menuepunkte, jetzt hinter den Icon-Buttons unter dem Logo (Reihenfolge =
   *  Icon-Reihe). `children` schaltet die Sektion um (siehe `selectSection`); `path` navigiert direkt. */
  switcherItems = computed<SwitcherItem[]>(() => {
    this.authService.authStateChanged();
    return [
      {
        key: 'tickets',
        label: 'Tickets',
        icon: 'fact_check',
        permission: { or: ['tickets.read'] },
        children: [
          { label: 'Alle Tickets', path: ["tickets"] },
          { label: 'Offene Tickets', path: ["tickets", "state", "0"] },
          { label: 'In Bearbeitung', path: ["tickets", "state", "1"] },
          { label: 'Geschlossene Tickets', path: ["tickets", "state", "4"] },
          { label: 'Archiviert', path: ["tickets", "archived"] },
          { label: 'Spam', path: ["tickets", "spam"] },
        ],
      },
      {
        key: 'crm',
        label: 'CRM',
        icon: 'trending_up',
        permission: { and: ['users.read'] },
        path: ["crm"],
      },
      {
        key: 'users',
        label: 'Personen',
        icon: 'people',
        permission: { and: ['users.read'] },
        children: [
          {
            label: 'Personenübersicht',
            expanded: false,
            path: ["users"],
            permission: { and: ['users.read'] }
          },
          {
            label: 'Tags',
            expanded: false,
            path: ["users", "tags"],
            permission: { and: ['users.read'] }
          },
          {
            label: 'Import',
            expanded: false,
            path: ["users", "import"],
            permission: { and: ['users.write'] },
          },
          {
            label: 'Import-Dubletten',
            expanded: false,
            path: ["users", "import", "drafts"],
            permission: { and: ['users.write'] },
          }
        ],
      },
      {
        key: 'bildbox',
        label: 'Fotobox',
        icon: 'photo',
        permission: { or: ['users.read', 'users.write'] },
        path: ["bildbox"],
      },
      {
        key: 'network',
        label: 'Netzwerk',
        icon: 'hub',
        // Geschuetzter Bereich: nur mit dem Netzwerk-Recht (oder Administration) sichtbar.
        permission: { or: ['network.manage', 'team.manage'] },
        path: ["network"],
      },
      {
        key: 'chatbot',
        label: 'Chatbot',
        icon: 'robot_2',
        permission: { or: ['chatbot.manage'] },
        path: ["chatbot-overview"],
      },
      {
        key: 'automation',
        label: 'Automation',
        icon: 'bolt',
        // Sichtbar, wenn eines der vier Kinder sichtbar waere - die Kinder gaten sich zusaetzlich
        // einzeln (siehe showSwitcherItem/TreeView.showNode).
        // So weit wie die Kinder, nicht weiter: Integrationen brauchen studios.write, Automationen
        // `automation`. Mit nur studios.read gab es eine Kachel, hinter der kein Eintrag lag.
        permission: { or: ['studios.write', 'automation'] },
        children: [
          {
            label: 'Integrationen',
            expanded: false,
            permission: { or: ['studios.write'] },
            path: ["settings", "integrations"]
          },
          {
            label: 'Nachrichtenvorlagen',
            expanded: false,
            permission: { or: ['studios.write'] },
            path: ["settings", "integrations", "templates"]
          },
          {
            label: 'Systemvorlagen',
            expanded: false,
            permission: { or: ['studios.write'] },
            path: ["settings", "integrations", "managed-templates"]
          },
          {
            label: 'Automationen',
            expanded: false,
            permission: { or: ['automation'] },
            path: ["settings", "integrations", "automation"]
          },
        ],
      },
      {
        key: 'marketing',
        label: 'Marketing',
        icon: 'campaign',
        // Sichtbar, sobald EINES der Marketing-Features an ist – Landing Pages, Newsletter
        // oder die MarketingBox (contentBox). Sonst würde mit dem einen auch das andere
        // verschwinden. Die Kinder gaten sich zusätzlich einzeln.
        show: this.authService.hasGroup('landingPage') || this.authService.hasFeature('newsletter') || this.authService.hasFeature('contentBox'),
        permission: { or: ['marketing.manage'] },
        children: [
          {
            label: 'Newsletter',
            show: this.authService.hasFeature('newsletter'),
            path: ["newsletter"],
          },
          {
            label: 'Landing Page Generator',
            path: ["landing-page-generator"],
            show: this.authService.hasGroup('landingPage'),
          },
          {
            label: 'Landing Pages Übersicht',
            path: ["landing-pages"],
            show: this.authService.hasGroup('landingPage'),
          },
          {
            // Nach den Landingpages: erst die Aktion bauen, dann sie auswerten. Das Einloesen
            // eines Gewinncodes sitzt oben auf derselben Seite und bekommt keinen eigenen
            // Eintrag - es waere ein Reiter, der nichts Neues zeigt.
            label: 'Gewinnspiele',
            path: ["gewinnspiele"],
            show: this.authService.hasGroup('landingPage'),
          },
          {
            label: 'Geomarketing',
            path: ["geomarketing"],
            permission: { and: ['users.read'] },
          },
          // MarketingBox. Reihenfolge = Arbeitsablauf: erst den Monat planen, dann
          // schreiben, dann Geld ausgeben. Das Profil steht zuletzt, weil man es einmal
          // ausfüllt und danach selten anfasst.
          {
            label: 'Postplan',
            path: ["marketing", "postplan"],
            show: this.authService.hasFeature('contentBox'),
          },
          {
            label: 'Content für Instagram',
            path: ["marketing", "content"],
            show: this.authService.hasFeature('contentBox'),
          },
          {
            label: 'Werbekampagnen (Meta)',
            path: ["marketing", "werbung"],
            show: this.authService.hasFeature('contentBox'),
          },
          {
            label: 'Marketingprofil',
            path: ["marketing", "profil"],
            show: this.authService.hasFeature('contentBox'),
          },
        ],
      },
      {
        key: 'selfservice',
        label: 'Self-Service',
        icon: 'how_to_reg',
        permission: { or: ['selfservice.manage'] },
        path: ["selfservice"],
      },
      {
        key: 'training',
        label: 'Training',
        icon: 'fitness_center',
        show: this.authService.hasFeature('train'),
        permission: { or: ['train.read'] },
        children: [
          {
            label: 'Trainingsprogramme',
            path: ["training", "programs"],
            permission: { or: ['train.read'] },
          },
          {
            label: 'Übungen / Geräte',
            path: ["training", "exercises"],
            permission: { or: ['train.read'] },
          },
          {
            label: 'Geräte-Katalog',
            path: ["training", "catalog"],
            permission: { or: ['train.read'] },
          },
          {
            label: 'Konfiguration',
            path: ["training", "configuration"],
            permission: { or: ['train.read'] },
          },
        ],
      },
      {
        // Finance: the studio's feature flag `finance`, still in its test phase.
        key: 'finance',
        label: 'Finanzen',
        icon: 'account_balance',
        badge: 'Testphase',
        show: this.authService.hasFeature('finance'),
        permission: { or: ['finance.read'] },
        children: [
          { label: 'Übersicht', path: ["finance"], permission: { or: ['finance.read'] } },
          { label: 'Umsatz & Kosten', path: ["finance", "entry"], permission: { or: ['finance.read'] } },
          { label: 'Mitglieder & Stammdaten', path: ["finance", "members"], permission: { or: ['finance.read'] } },
          { label: 'Controlling-Liste', path: ["finance", "controlling"], permission: { or: ['finance.read'] } },
          { label: 'Laufzeiten & Kündigungen', path: ["finance", "laufzeiten"], permission: { or: ['finance.read'] } },
          { label: 'Reha-Auswertung', path: ["finance", "reha-auswertung"], permission: { or: ['finance.read'] } },
          { label: 'Statistik', path: ["finance", "stats"], permission: { or: ['finance.read'] } },
          // Consultants: off by default -- needs the consultant right on top of finance.read.
          { label: 'Berater & Kommentare', path: ["finance", "sharing"], permission: { and: ['finance.read', 'consultant.read'] } },
        ],
      },
      {
        // Consultant area: only for tenants with the feature flag `consultant` and users with consultant.read.
        key: 'consultant',
        label: 'Berater',
        icon: 'business_center',
        show: this.authService.hasFeature('consultant'),
        permission: { or: ['consultant.read'] },
        children: [
          { label: 'Mandanten', path: ["consultant"] },
          { label: 'Vergleich', path: ["consultant", "compare"] },
          { label: 'Einladungen', path: ["consultant", "invites"] },
        ],
      },
      {
        // Nur für Vierkant-Betreiber (Authentik-Entitlement), nie für Studio-Rollen sichtbar.
        key: 'catalog-admin',
        label: 'Master',
        icon: 'fitness_center',
        show: this.authService.hasFeature('train'),
        permission: { or: ['catalog.manage'] },
        path: ["catalog-admin"]
      },
    ];
  });

  /** Aktuelle URL als Signal, um die Switcher-Sektion beim direkten Aufruf/Reload automatisch auf den
   *  passenden Bereich zu setzen (siehe `lastAppSection`). */
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  /** Zuletzt über Route oder manuellen Klick bestimmte Sektion - eine Anwendung aus `switcherItems`
   *  oder die Einstellungen (siehe `selectSection`/`selectSettings` und den Navigations-Effect im
   *  Konstruktor). Bleibt unveraendert, wenn man zu einer Seite ausserhalb aller Sektionen navigiert
   *  (Dashboard, Benachrichtigungen, ...) - `activeSection` leitet daraus immer einen gueltigen Wert
   *  ab. */
  private readonly lastAppSection = signal<string | null>(null);

  /** Welche Sektion gerade als aktiv gilt - fuer die Hervorhebung des Icon-Buttons (bzw. des
   *  Einstellungen-Eintrags im Footer) und, bei Eintraegen mit `children`, fuer das Einblenden der
   *  zugehoerigen Liste. Es ist immer eine Sektion ausgewaehlt, sobald mindestens ein Switcher-Item
   *  sichtbar ist: `lastAppSection`, oder,
   *  falls noch keine Route/Klick eine Sektion bestimmt hat (z.B. direkter Aufruf von Dashboard beim
   *  allerersten Login), die erste sichtbare Anwendung aus der Liste. */
  activeSection = computed<string | null>(() => this.lastAppSection() ?? this.firstVisibleSwitcherKey());

  /** Ueberschrift (Label + Icon) und Kinder der aktuell aktiven Sektion. Die Ueberschrift
   *  erscheint immer, sobald eine Sektion aktiv ist - auch ohne eigene Unterpunkte (z.B. CRM,
   *  Netzwerk) -, damit im Menue immer erkennbar bleibt, wo man sich befindet; `children` ist dann
   *  leer. `null` nur, wenn ueberhaupt keine Anwendung sichtbar/aktiv ist. */
  activeSectionNodes = computed<{ label: string; icon: string; children: TreeNode[] } | null>(() => {
    const key = this.activeSection();
    // Einstellungen nur dann als moegliche Sektion, wenn der Nutzer sie ueberhaupt sehen darf -
    // sonst zeigte ein direkter Aufruf einer Einstellungen-Route (die Routen haengen nur am
    // AuthGuard, nicht an einem Recht) eine Ueberschrift ueber einer komplett weggefilterten Liste.
    const sections = this.settingsVisible()
      ? [...this.switcherItems(), this.settingsSection()]
      : this.switcherItems();
    const item = sections.find(i => i.key === key);
    // Ueberschrift zeigt IMMER die aktive Anwendung, auch ohne eigene Unterpunkte (z.B. CRM, Netzwerk)
    // - sonst weiss der Nutzer dort nicht, wo im Menue er sich befindet.
    return item ? { label: item.label, icon: item.icon, children: item.children ?? [] } : null;
  });

  constructor() {
    const claims = this.oauth.getIdentityClaims();
    this.userInfo.set(claims?.email || claims?.nickname || '');
    this.sub.add(
      this.oauth.events
      .pipe(filter((e: OAuthEvent) => e.type === 'token_received'))
      .subscribe(() => {
        const claims: any = this.oauth.getIdentityClaims();
        this.userInfo.set(claims?.email || claims?.nickname || '');
      })
    );
    // Sobald zu einer Seite navigiert wird, die zu einer Switcher-Sektion gehoert, uebernimmt die
    // Menue-Sektion diese (auch durch selectSection selbst ausgeloest). Fuehrt die Navigation zu
    // keiner Sektion (Dashboard, Einstellungen, ...), bleibt die zuletzt aktive Anwendung bestehen -
    // es soll immer eine Anwendung ausgewaehlt sein.
    effect(() => {
      const section = this.sectionForUrl(this.currentUrl());
      if (section) this.lastAppSection.set(section);
      this.closeSwitcher();
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  /** Klappt die App-Switcher-Sektion unter dem Kopfbereich auf bzw. zu. Sie bleibt Teil der
   *  Menueleiste - die Navigation darunter wird verschoben, nicht ueberdeckt; die Animation steckt
   *  komplett im CSS (siehe .switcher-section). */
  toggleSwitcher() {
    this.switcherOpen.update(open => !open);
  }

  closeSwitcher() {
    // Beim Zuklappen wird die Sektion inert, und inert entfernt den Fokus aus seinem Teilbaum - er
    // fiele ersatzlos auf <body> zurueck, die Tab-Reihenfolge begaenne danach wieder ganz oben.
    // Deshalb hier zurueck auf den Button, der die Sektion geoeffnet hat. Fuer Mausklicks ist das
    // unsichtbar (der Fokusring haengt an :focus-visible), fuer Tastatur-Bedienung der Unterschied
    // zwischen "weiter im Menue" und "Fokus verloren".
    const section = this.switcherSection?.nativeElement;
    if (section?.contains(document.activeElement)) {
      this.switcherTrigger?.nativeElement.focus();
    }
    this.switcherOpen.set(false);
  }

  /** Footer-Eintrag "Einstellungen": schaltet das Menue auf die Einstellungen-Sektion um, genau wie
   *  ein Klick auf einen App-Switcher-Button mit Kindern - inklusive Auswahl des ersten sichtbaren
   *  Unterpunkts. */
  selectSettings() {
    // Wie bei den Switcher-Buttons explizit und nicht ueber den Navigations-Effect: steht man
    // schon auf der Zielroute, navigiert der Router nicht (onSameUrlNavigation: 'ignore'), es
    // kommt kein NavigationEnd - die Sektion bliebe offen stehen.
    this.closeSwitcher();
    this.selectSection(this.settingsSection());
  }

  private sectionForUrl(url: string): string | null {
    const segments = url.split('?')[0].split('#')[0].split('/').filter(Boolean);
    // "settings/integrations..." gehoert zur Automation-Sektion, die anderen "settings/..."-Routen
    // (Studio, Standorte, ...) zur Einstellungen-Sektion - deshalb hier ueber die ersten zwei
    // Segmente statt nur ueber das erste entschieden. Die Sektion einer Route zaehlt nur, wenn die
    // Route ihr eindeutig gehoert; sonst lieber null (= vorige Sektion behalten), siehe 'forms'.
    if (segments[0] === 'settings' && segments[1] === 'integrations') return 'automation';
    // Die Routen der Einstellungen-Sektion (siehe settingsNodes) - damit auch ein direkter Aufruf
    // oder ein Reload dort die Einstellungen als aktive Sektion zeigt. Bewusst nur die verlinkten
    // Pfade und nicht alle "settings/..."-Routen: 'settings/lead-stages' etwa hat im
    // Einstellungen-Menue keinen Eintrag (die Route leitet auf 'crm/stufen' um).
    if (segments[0] === 'settings' && ['studio', 'locations', 'email-accounts', 'reply-templates', 'integrationen'].includes(segments[1] ?? '')) {
      return SETTINGS_SECTION_KEY;
    }
    // '/forms' ist die Bogen-Uebersicht aus den Einstellungen, '/forms/<id>' dagegen der Editor -
    // und den oeffnet auch Self-Service (selfservice-admin: createCustom/editTile). Wuerde der die
    // Sektion umschalten, wechselte das Menue mitten im Self-Service-Ablauf nach Einstellungen.
    if (segments[0] === 'forms') return segments.length === 1 ? SETTINGS_SECTION_KEY : null;
    const segment = segments[0] ?? '';
    const bySegment: Record<string, string> = {
      'tickets': 'tickets',
      'ticket-detail': 'tickets',
      'crm': 'crm',
      'users': 'users',
      'bildbox': 'bildbox',
      'network': 'network',
      'chatbot-overview': 'chatbot',
      'chatbot-edit': 'chatbot',
      'newsletter': 'marketing',
      'landing-page-generator': 'marketing',
      'landing-pages': 'marketing',
      'geomarketing': 'marketing',
      'marketing': 'marketing',
      'selfservice': 'selfservice',
      'training': 'training',
      'catalog-admin': 'catalog-admin',
      'category-groups': SETTINGS_SECTION_KEY,
      'team': SETTINGS_SECTION_KEY,
    };
    return bySegment[segment] ?? null;
  }

  showSwitcherItem(item: SwitcherItem): boolean {
    if (item.show === false) return false;
    if (!item.permission) return true;
    return this.authService.hasPermission(item.permission);
  }

  /** Erstes sichtbares Switcher-Item aus der Liste - Fallback fuer `activeSection`, solange noch
   *  keine Route/kein Klick eine Anwendung bestimmt hat (siehe `lastAppSection`). */
  private firstVisibleSwitcherKey(): string | null {
    return this.switcherItems().find(item => this.showSwitcherItem(item))?.key ?? null;
  }

  selectSection(item: SwitcherItem) {
    if (!item.children) return;
    const target = this.firstVisibleChild(item.children);
    // Ohne einen fuer diesen Nutzer sichtbaren Unterpunkt gar nicht umschalten: die Sektion zeigte
    // sonst nur ihre Ueberschrift ueber einer leer gefilterten Liste - und bliebe dort haengen, weil
    // ohne Navigation auch kein NavigationEnd folgt, das lastAppSection wieder korrigieren koennte.
    if (!target?.path) return;
    this.lastAppSection.set(item.key);
    // Genau das, was TreeView.selectNode() bei einem Klick auf den Eintrag selbst täte - hier
    // nachgezogen, weil dieser Klick den Eintrag nie direkt anfasst, ihn aber trotzdem aktiv macht.
    this.selectedNode.set(target.label);
    this.router.navigate(target.path);
  }

  /** Erster fuer den aktuellen Nutzer sichtbarer Eintrag einer Sektion (gleiche Sichtbarkeitsregel
   *  wie im TreeView-Template: `show !== false` und, falls gesetzt, `permission` erfuellt) - dahin
   *  wird beim Klick auf den Switcher-Button navigiert. */
  private firstVisibleChild(nodes: TreeNode[]): TreeNode | undefined {
    return nodes.find(n => n.show !== false && (!n.permission || this.authService.hasPermission(n.permission)));
  }

  logoutDialogButtons = [
    {
      label: 'Ja',
      toolTip: 'Ausloggen',
      style: "filled",
      command: ( ) => {
        this.logout()
      }
    },
    {
      label: 'Nein',
      toolTip: 'Eingeloggt bleiben',
      style: "tonal",
      command: ( ) => {
        this.dialog.closeAll();
      }
    }
  ]

  // #studioSub: Subscription;

  openNotifications() {
    this.router.navigate(['/notifications']);
  }

  openLogoutDialog(enterAnimationDuration: string, exitAnimationDuration: string): void {
    const portal = new TemplatePortal(this.logoutTemplate, this.vcr);
    const ref = this.dialog.open(Dialog, {
      data: { buttons: this.logoutDialogButtons, title: 'Abmelden' },
      width: 'fit-content',
      enterAnimationDuration,
      exitAnimationDuration,
    });
    ref.componentInstance.content = portal;
  }

  logout() {
    this.oauth.logOut();
  }
}
